import React from 'react';
import { Link } from 'react-router-dom';
import type { SetupStatusResponse } from '../services/apiService';

// A feed the scheduler gave up on, said where the publisher will actually look.
//
// The Product feeds page already reports this well — "Paused — Feed source
// responded with HTTP 404", in red, next to Resume sync. The problem was that
// NOTHING else did: the dashboard and Analytics read no feed-sync state at all,
// so a publisher whose feed struck out days ago saw the same all-zero,
// still-collecting framing as one who signed up an hour ago. The two states
// were rendered identically on every surface a publisher visits routinely, and
// the one page that could tell them apart had to be sought out deliberately.
//
// DELIBERATELY NOT INSIDE ActivationChecklist. That component renders null once
// setup is complete — which is precisely the publisher this banner exists for,
// the one who finished onboarding weeks ago and has been quietly serving
// nothing since. Putting it there would hide it from everyone who needs it.
//
// That independence used to be bought with a SECOND request to the endpoint the
// checklist was already calling. It is not any more — see the props below.

// Publisher-facing names. Kept short deliberately: this banner names the network
// so they know where to go, and NetworksPage carries the rest.
const NETWORK_LABELS: Record<string, string> = {
  awin: 'Awin',
  cj: 'Commission Junction',
  impact: 'Impact',
  rakuten: 'Rakuten',
  '2performant': '2Performant',
};

// NAME: still FeedHealthBanner, now carrying a third cause that is not a feed.
// Considered renaming and did not: "feed" is this portal's publisher-facing word
// for the whole supply-and-earnings pipeline (the nav item is "Product feeds"),
// a connection exists to measure what feeds earn, and the rename churns ~25 test
// lines for no reader benefit. Recorded so the next reader sees a decision
// rather than an oversight.
//
// Props, not a fetch. This component and ActivationChecklist read the SAME
// endpoint, and the page now calls it once and hands both of them the result —
// six queries per dashboard load became three. What the old comment defended was
// RENDER independence ("independence from the checklist's render decisions",
// since the checklist returns null exactly when this banner matters most), and
// that is untouched: these are siblings, each deciding on its own what to show.
// A null status — still loading, or the fetch failed — renders nothing, which is
// the same silence the old .catch(() => {}) chose, and for the same reason: a
// warning ABOUT something else must not become a second thing to worry about.
export interface FeedHealthBannerProps {
  status: SetupStatusResponse | null;
}

const FeedHealthBanner: React.FC<FeedHealthBannerProps> = ({ status }) => {
  const paused = status?.pausedFeeds ?? [];
  const stale = status?.staleUploads ?? [];
  const broken = status?.brokenConnections ?? [];
  const staleDays = status?.staleUploadDays ?? 0;

  if (paused.length === 0 && stale.length === 0 && broken.length === 0) return null;

  return (
    <section
      className="stats-section"
      data-testid="feed-health-banner"
      role="status"
      style={{
        borderLeft: '4px solid var(--warn, #9A5B00)',
        background: 'var(--warn-bg, #FFFBEB)',
      }}
    >
      {/* TWO CAUSES, ONE CONSEQUENCE — a frozen catalogue. Reported separately
          because the remedies differ: a struck-out feed is fixed by repairing its
          URL or resuming it, an old upload only by uploading again. Each block is
          self-contained, so either can render alone. */}
      {paused.length > 0 && (
        <>
          <h2 style={{ marginTop: 0 }}>
            {paused.length === 1
              ? 'A product feed stopped updating'
              : `${paused.length} product feeds stopped updating`}
          </h2>
          <p style={{ marginBottom: '0.75rem' }}>
            We tried repeatedly and gave up, so {paused.length === 1 ? 'its' : 'their'} products are
            frozen at the last successful sync — overlays keep serving, but nothing new arrives and
            anything withdrawn since then is still being shown.
          </p>
          <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.1rem' }}>
            {paused.map(f => (
              <li key={f.id} data-testid={`paused-feed-${f.id}`} style={{ marginBottom: '0.3rem' }}>
                <strong>{f.label}</strong> <span className="no-data">({f.network})</span>
                <div className="no-data" style={{ fontSize: '0.875rem' }}>
                  {f.lastError}
                  {f.lastSyncedAt && ` — last succeeded ${new Date(f.lastSyncedAt).toLocaleDateString()}`}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {stale.length > 0 && (
        <div data-testid="stale-uploads">
          <h2 style={{ marginTop: paused.length > 0 ? '1rem' : 0 }}>
            {stale.length === 1
              ? 'An uploaded product file is out of date'
              : `${stale.length} uploaded product files are out of date`}
          </h2>
          {/* No failure happened here, so the wording must not imply one. What is
              true is narrower and worse: a file was never going to refresh. */}
          <p style={{ marginBottom: '0.75rem' }}>
            {stale.length === 1 ? 'It was' : 'They were'} uploaded more than {staleDays} days ago and
            {stale.length === 1 ? ' has' : ' have'} not been replaced since. An uploaded file never
            refreshes on its own, so prices and availability drift while overlays keep serving.
            Connecting the shop to your network instead keeps it up to date daily.
          </p>
          <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.1rem' }}>
            {stale.map(f => (
              <li key={f.id} data-testid={`stale-upload-${f.id}`} style={{ marginBottom: '0.3rem' }}>
                <strong>{f.label}</strong> <span className="no-data">({f.network})</span>
                {f.lastUploadAt && (
                  <div className="no-data" style={{ fontSize: '0.875rem' }}>
                    uploaded {new Date(f.lastUploadAt).toLocaleDateString()}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {broken.length > 0 && (
        <div data-testid="broken-connections">
          <h2 style={{ marginTop: (paused.length > 0 || stale.length > 0) ? '1rem' : 0 }}>
            {broken.length === 1
              ? 'A network stopped reporting your earnings'
              : `${broken.length} networks stopped reporting your earnings`}
          </h2>
          {/* The consequence differs from the two above and has to be said, or
              this reads as another frozen-catalogue warning: overlays are FINE.
              What stopped is the measurement, which is invisible everywhere else
              precisely because nothing about the site changes. */}
          <p style={{ marginBottom: '0.75rem' }}>
            Your overlays are unaffected and still earning — but{' '}
            {broken.length === 1 ? 'this network' : 'these networks'} refused the credential we
            use to read commissions back, so what you earn is an estimate again until it is
            reconnected.
          </p>
          <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.1rem' }}>
            {broken.map(c => (
              <li key={c.id} data-testid={`broken-connection-${c.id}`} style={{ marginBottom: '0.3rem' }}>
                <strong>{NETWORK_LABELS[c.network] || c.network}</strong>
                {c.accountRef && <span className="no-data"> ({c.accountRef})</span>}
              </li>
            ))}
          </ul>
          <Link to="/networks" data-testid="broken-connection-cta">Reconnect on Your networks →</Link>
        </div>
      )}

      {/* LAND ON THE FEED, NOT ON A LIST. `/feeds` orders shops by the merchant
          prior, which knows nothing about our own sync state — on the live
          account the one stopped feed sat at position 18 of 32, about seven
          phone screens below where this link used to arrive.

          Addressed `feed:<id>` because that id is stable and this route already
          has it; `Shop.key` re-keys when a feed's advertiser resolves, and
          every feed re-keys at once when the entitlement sweep goes quiet for
          seven days — which is exactly the incident this banner announces. See
          `utils/shopRef.ts`.

          Only when there is ONE of them. With several, the hub is the right
          destination: it now groups them all at the top. */}
      {(paused.length > 0 || stale.length > 0) && (
        paused.length + stale.length === 1 ? (
          <Link
            to={`/feeds/${encodeURIComponent(`feed:${(paused[0] ?? stale[0]).id}`)}`}
            data-testid="feed-health-cta"
          >
            Fix {(paused[0] ?? stale[0]).label} →
          </Link>
        ) : (
          <Link to="/feeds" data-testid="feed-health-cta">Fix it on Product feeds →</Link>
        )
      )}
    </section>
  );
};

export default FeedHealthBanner;
